import React, { useRef, useState, useEffect } from 'react';
import PdfViewer from './Pdfviewer';
import { motion, AnimatePresence } from 'framer-motion';
import { FiSun, FiMoon, FiX, FiMenu } from 'react-icons/fi';
import { FaChevronRight } from 'react-icons/fa';
import Swal from 'sweetalert2';
import './App.css';

const showToast = (title, icon = 'info') => {
  Swal.fire({
    toast: true,
    position: 'bottom-end',
    showConfirmButton: false,
    timer: 1500,
    timerProgressBar: true,
    icon,
    title,
  });
};

const placeholderSuggestions = [
  'PRE-K 1-10...',
  'K 51-60...',
  'Grade 1 1X...',
  'Grade 2 2Y 101-110...',
  'Grade 3 3X...',
  'Grade 4 4Y 151-160...',
  'Grade 5 5X...',
  '1Y 21-30...',
  'Pre-K 191-200...',
  'Grade 1...',
  '2X 71-80...',
  'K 121-130...'
];

// Build levels structure
const levels = {
  'PRE-K': {
    subfolders: {
      'PRE-K': {
        booklets: []
      }
    }
  },
  'K': {
    subfolders: {
      'K': {
        booklets: []
      }
    }
  }
};

// Generate Pre-K booklets (PRE-K_1-10 AK to PRE-K_191-200 AK) - WITH space before AK
for (let i = 1; i <= 191; i += 10) {
  const rangeEnd = Math.min(i + 9, 200);
  levels['PRE-K'].subfolders['PRE-K'].booklets.push(`PRE-K_${i}-${rangeEnd} AK`);
}

// Generate K booklets (K_1-10 AK to K_191-200 AK) - WITH space before AK
for (let i = 1; i <= 191; i += 10) {
  const rangeEnd = Math.min(i + 9, 200);
  levels['K'].subfolders['K'].booklets.push(`K_${i}-${rangeEnd} AK`);
}

// Generate Grade 1-5 with X and Y subfolders
for (let grade = 1; grade <= 5; grade++) {
  const gradeKey = `Grade ${grade}`;
  levels[gradeKey] = {
    subfolders: {
      [`${grade}X`]: { booklets: [] },
      [`${grade}Y`]: { booklets: [] }
    }
  };
  
  // Generate X booklets (L1X_1-10 AK to L1X_191-200 AK)
  for (let i = 1; i <= 191; i += 10) {
    const rangeEnd = Math.min(i + 9, 200);
    levels[gradeKey].subfolders[`${grade}X`].booklets.push(`L${grade}X_${i}-${rangeEnd} AK`);
  }
  
  // Generate Y booklets
  // Grade 3, 4, 5 Y files don't have AK suffix (just L3Y_1-10.pdf)
  // Grade 1, 2 Y files have space AK suffix (L1Y_1-10 AK.pdf)
  for (let i = 1; i <= 191; i += 10) {
    const rangeEnd = Math.min(i + 9, 200);
    if (grade >= 3) {
      // Grade 3, 4, 5: No AK suffix for Y files
      levels[gradeKey].subfolders[`${grade}Y`].booklets.push(`L${grade}Y_${i}-${rangeEnd}`);
    } else {
      // Grade 1, 2: Has space AK suffix for Y files
      levels[gradeKey].subfolders[`${grade}Y`].booklets.push(`L${grade}Y_${i}-${rangeEnd} AK`);
    }
  }
}

const getUserName = () => "Tutor";

function Home({ onLogout }) {
  const [animatedPlaceholder, setAnimatedPlaceholder] = useState('');
  const [placeholderIndex, setPlaceholderIndex] = useState(0);
  const [sidebarVisible, setSidebarVisible] = useState(window.innerWidth > 1024);
  const [selectedLevel, setSelectedLevel] = useState('PRE-K');
  const [selectedSubfolder, setSelectedSubfolder] = useState('PRE-K');
  const [selectedBooklet, setSelectedBooklet] = useState('PRE-K_1-10 AK');
  const [expandedLevels, setExpandedLevels] = useState({ 'PRE-K': true });
  const [expandedSubfolders, setExpandedSubfolders] = useState({ 'PRE-K': true });
  const [darkMode, setDarkMode] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isDesktop, setIsDesktop] = useState(window.innerWidth > 1024);
  const bookletRef = useRef();

  const toggleSidebar = () => setSidebarVisible(prev => !prev);

  useEffect(() => {
    const handleResize = () => {
      setIsDesktop(window.innerWidth > 1024);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      setPlaceholderIndex((prev) => (prev + 1) % placeholderSuggestions.length);
    }, 3000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const current = placeholderSuggestions[placeholderIndex];
    let charIndex = 0;
    const typeInterval = setInterval(() => {
      setAnimatedPlaceholder(current.slice(0, charIndex));
      charIndex++;
      if (charIndex > current.length) clearInterval(typeInterval);
    }, 50);
    return () => clearInterval(typeInterval);
  }, [placeholderIndex]);

  useEffect(() => {
    requestAnimationFrame(() => {
      if (bookletRef.current) bookletRef.current.scrollTop = 0;
    });
  }, [selectedBooklet]);

  const currentLevel = levels[selectedLevel];
  const currentSubfolderData = currentLevel?.subfolders?.[selectedSubfolder];

  // Build PDF source path - FIXED VERSION
  let bookletSrc = '';
  if (selectedBooklet) {
    // Remove any existing .pdf extension and add it properly
    const cleanBooklet = selectedBooklet.replace('.pdf', '');
    
    if (selectedLevel === 'PRE-K' || selectedLevel === 'K') {
      bookletSrc = `/pdfs/${encodeURIComponent(selectedLevel)}/${cleanBooklet}.pdf`;
    } else {
      bookletSrc = `/pdfs/${encodeURIComponent(selectedLevel)}/${selectedSubfolder}/${cleanBooklet}.pdf`;
    }
  }

  // Debug: log the PDF path
  useEffect(() => {
    console.log('Loading PDF:', bookletSrc);
  }, [bookletSrc]);

  // Dummy scroll handler (required by PdfViewer but not used)
  const handleScroll = (scrollTop) => {
    // No-op since we don't need scroll syncing
  };

  const handleLogoutClick = () => {
    Swal.fire({
      title: 'Are you sure?',
      text: 'You will be logged out.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Yes, logout',
      cancelButtonText: 'Cancel',
    }).then((result) => {
      if (result.isConfirmed) {
        sessionStorage.removeItem('isLoggedIn');
        sessionStorage.removeItem('userEmail');
        onLogout();
      }
    });
  };

  useEffect(() => {
    const savedDarkMode = sessionStorage.getItem('darkMode');
    if (savedDarkMode === 'true') {
      setDarkMode(true);
      document.body.classList.add('dark-mode');
    }
  }, []);

  useEffect(() => {
    const handleContextMenu = (e) => e.preventDefault();
    const handleKeyDown = (e) => {
      if ((e.ctrlKey || e.metaKey) && (e.key === 'p' || e.key === 's')) {
        e.preventDefault();
        alert('This action is disabled.');
      }
    };
    window.addEventListener('contextmenu', handleContextMenu);
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('contextmenu', handleContextMenu);
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  const handleSearch = (input) => {
    setSearchTerm(input);
    const normalizedInput = input
      .toLowerCase()
      .replace(/[_–—]/g, ' ')
      .replace(/\s+/g, ' ')
      .replace(/\bpre\s*k\b/gi, 'pre-k')
      .replace(/\bgrade\s*/gi, 'grade ')
      .replace(/\.{2,}/g, '')
      .trim();

    if (!normalizedInput) {
      setSearchResults([]);
      return;
    }

    const results = [];
    const added = new Set();

    // Search through all levels and subfolders
    Object.entries(levels).forEach(([levelKey, content]) => {
      // Search in subfolders
      if (content.subfolders) {
        Object.entries(content.subfolders).forEach(([subfolderName, subfolderData]) => {
          subfolderData.booklets.forEach((booklet) => {
            const display = `${levelKey} ${subfolderName} ${booklet}`.toLowerCase();
            const bookletDisplay = booklet.replace(/_/g, ' ').toLowerCase();
            
            if (display.includes(normalizedInput) || bookletDisplay.includes(normalizedInput)) {
              const key = `${levelKey}_${subfolderName}_${booklet}`;
              if (!added.has(key)) {
                results.push({ level: levelKey, subfolder: subfolderName, booklet });
                added.add(key);
              }
            }
          });
        });
      }
    });

    setSearchResults(results);
  };

  const toggleDarkMode = () => {
    setDarkMode((prev) => {
      const newMode = !prev;
      sessionStorage.setItem('darkMode', newMode);
      if (newMode) document.body.classList.add('dark-mode');
      else document.body.classList.remove('dark-mode');
      return newMode;
    });
  };

  const userEmail = sessionStorage.getItem('userEmail');
  const userName = getUserName(userEmail);

  return (
    <div className={`app-container ${darkMode ? 'dark' : ''}`}>
      <div className={`sidebar ${sidebarVisible ? 'visible' : 'hidden'}`}>
        <div className="welcome-card">👋 Hello {userName}!</div>
        <div className="level-scroll-area">
          {Object.entries(levels).map(([level, content]) => {
            const isExpanded = expandedLevels[level];

            return (
              <div key={level}>
                <button
                  className={`level-btn ${selectedLevel === level ? 'active' : ''}`}
                  onClick={() => {
                    const firstSubfolder = Object.keys(content.subfolders)[0];
                    const firstBooklet = content.subfolders[firstSubfolder]?.booklets[0];
                    const newState = !isExpanded;
                    setSelectedLevel(level);
                    setSelectedSubfolder(firstSubfolder);
                    setSelectedBooklet(firstBooklet);
                    setExpandedLevels({ [level]: newState });
                    setExpandedSubfolders({ [firstSubfolder]: true });
                    showToast(`${level} ${newState ? 'expanded' : 'collapsed'}`, 'info');
                  }}
                >
                  {level}
                  <span className={`arrow ${isExpanded ? 'rotate' : ''}`}>
                    <FaChevronRight />
                  </span>
                </button>

                <AnimatePresence initial={false}>
                  {isExpanded && (
                    <motion.div
                      className="booklet-list"
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.3 }}
                    >
                      {/* Subfolders */}
                      {Object.entries(content.subfolders).map(([subfolderName, subfolderData]) => {
                        const isSubfolderExpanded = expandedSubfolders[subfolderName];
                        
                        // For PRE-K and K, don't show subfolder button, just show booklets directly
                        if (level === 'PRE-K' || level === 'K') {
                          return (
                            <div key={subfolderName}>
                              {subfolderData.booklets.map((booklet) => (
                                <button
                                  key={booklet}
                                  className={`booklet-btn ${selectedBooklet === booklet ? 'selected' : ''}`}
                                  onClick={() => {
                                    setSelectedLevel(level);
                                    setSelectedSubfolder(subfolderName);
                                    setSelectedBooklet(booklet);
                                  }}
                                >
                                  {booklet.replace(/_/g, ' ').replace(' AK', '')}
                                </button>
                              ))}
                            </div>
                          );
                        }
                        
                        // For Grades 1-5, show subfolder with expand/collapse
                        return (
                          <div key={subfolderName}>
                            <button
                              className={`wp-level-btn ${selectedSubfolder === subfolderName ? 'active' : ''}`}
                              onClick={() => {
                                const firstBooklet = subfolderData.booklets[0];
                                const expanded = !expandedSubfolders[subfolderName];
                                setSelectedLevel(level);
                                setSelectedSubfolder(subfolderName);
                                setSelectedBooklet(firstBooklet);
                                setExpandedSubfolders(prev => ({ ...prev, [subfolderName]: expanded }));
                                showToast(`${subfolderName} ${expanded ? 'expanded' : 'collapsed'}`, 'info');
                              }}
                            >
                              {subfolderName}
                              <span className={`arrow ${isSubfolderExpanded ? 'rotate' : ''}`}>
                                <FaChevronRight />
                              </span>
                            </button>
                            <AnimatePresence initial={false}>
                              {isSubfolderExpanded && (
                                <motion.div
                                  className="booklet-list"
                                  initial={{ height: 0, opacity: 0 }}
                                  animate={{ height: 'auto', opacity: 1 }}
                                  exit={{ height: 0, opacity: 0 }}
                                  transition={{ duration: 0.3 }}
                                >
                                  {subfolderData.booklets.map((booklet) => (
                                    <button
                                      key={booklet}
                                      className={`booklet-btn ${selectedBooklet === booklet ? 'selected' : ''}`}
                                      onClick={() => {
                                        setSelectedLevel(level);
                                        setSelectedSubfolder(subfolderName);
                                        setSelectedBooklet(booklet);
                                      }}
                                    >
                                      {booklet.replace(/_/g, ' ').replace(' AK', '')}
                                    </button>
                                  ))}
                                </motion.div>
                              )}
                            </AnimatePresence>
                          </div>
                        );
                      })}
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            );
          })}
        </div>
        <button className="logout-btn" onClick={handleLogoutClick}>Logout</button>
      </div>
      <AnimatePresence>
        {sidebarVisible && !isDesktop && (
          <motion.div
            className="sidebar-backdrop"
            initial={{ opacity: 0 }}
            animate={{ opacity: 0.5 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            onClick={() => setSidebarVisible(false)}
          />
        )}
      </AnimatePresence>
      <div className="right-container">
        <div className="top-bar">
          <button className="burger-btn" onClick={toggleSidebar}>
            <FiMenu size={24} />
          </button>
          <div className="search-bar">
            <div className="search-container">
              <input
                type="text"
                className="search-input"
                placeholder=""
                value={searchTerm}
                onChange={(e) => handleSearch(e.target.value)}
              />
              {searchTerm === '' && (
                <div className="placeholder-wrapper">
                  <span className="fixed-text">Search</span>
                  <span key={placeholderIndex} className="animated-text">
                    {animatedPlaceholder}
                  </span>
                </div>
              )}
              {searchTerm !== '' && (
                <button className="clear-button" onClick={() => handleSearch('')}>
                  <FiX size={20} />
                </button>
              )}
            </div>
          </div>
          <div className="button-box">
            <label className="custom-toggle">
              <input type="checkbox" checked={darkMode} onChange={toggleDarkMode} />
              <span className="slider">
                <FiSun className="icon sun" />
                <FiMoon className="icon moon" />
              </span>
            </label>
          </div>
        </div>
        {searchResults.length > 0 && (
          <div className="search-results">
            {searchResults.map((res, idx) => (
              <div
                key={idx}
                className="search-result-item"
                onClick={() => {
                  setSelectedLevel(res.level);
                  setSelectedSubfolder(res.subfolder);
                  setSelectedBooklet(res.booklet);
                  setExpandedLevels({ [res.level]: true });
                  if (res.subfolder && res.level !== 'PRE-K' && res.level !== 'K') {
                    setExpandedSubfolders({ [res.subfolder]: true });
                  }
                  setSearchResults([]);
                  setSearchTerm('');
                }}
              >
                <span>{res.level}</span>
                {res.level !== 'PRE-K' && res.level !== 'K' && (
                  <>
                    <FaChevronRight style={{ margin: '0 6px', fontSize: '12px' }} />
                    <span>{res.subfolder}</span>
                  </>
                )}
                <FaChevronRight style={{ margin: '0 6px', fontSize: '12px' }} />
                <span>{res.booklet.replace(/_/g, ' ').replace(' AK', '')}</span>
              </div>
            ))}
          </div>
        )}
        <div className="viewer-container">
          <div className="pdf-box">
            <PdfViewer
              src={bookletSrc}
              label={selectedBooklet ? selectedBooklet.replace(/_/g, ' ').replace(' AK', '') : ''}
              colorClass="blue-box"
              onScroll={handleScroll}
              scrollContainerRef={bookletRef}
              showSearch={true}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

export default Home;